import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  MRT_ColumnDef,
  MRT_SortingState,
  MRT_Updater,
  MaterialReactTable,
} from "material-react-table";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../theme";
import { ProductCard, ProductSort } from "../../types/productPlane";
import CompareToggle from "../Compare/CompareToggle";
import CapabilityChips from "./CapabilityChips";
import { formatCode, productName } from "./productFields";

export interface ProductResultTableProps {
  // The products of the page shown
  products: ProductCard[];
  // The order the server gave them in
  sort: ProductSort;
  onSortChange: (sort: ProductSort) => void;
  // Opens the product of a row. Without it a row is plain text.
  onOpen?: (product: ProductCard) => void;
  // The id of the product that is open beside the list; its row is marked
  openProductId?: string;
  // Whether another page or order is being loaded
  loading?: boolean;
}

// The columns the server can order by, each with its order. Both orders run
// from the most or the latest down; the server offers no other direction.
const SORT_OF_COLUMN: Record<string, ProductSort> = {
  source_count: "sources",
  latest_source: "latest",
};

const sortingOf = (sort: ProductSort): MRT_SortingState =>
  Object.entries(SORT_OF_COLUMN)
    .filter(([, columnSort]) => columnSort === sort)
    .map(([id]) => ({ id, desc: true }));

const monospaceCell = { sx: monospaceStyle };

// The products of a result list as a table: the fields of the result card in
// columns. The server orders the rows; the table only asks for an order.
const ProductResultTable: React.FC<ProductResultTableProps> = ({
  products,
  sort,
  onSortChange,
  onOpen,
  openProductId,
  loading = false,
}) => {
  const columns = useMemo<MRT_ColumnDef<ProductCard>[]>(
    () => [
      // The box that picks the product for the comparison: it neither sorts
      // nor opens the row
      {
        id: "compare",
        header: "",
        accessorFn: () => "",
        enableSorting: false,
        size: 48,
        muiTableHeadCellProps: { "aria-label": "Compare" },
        Cell: ({ row }) => (
          <CompareToggle id={row.original.id} name={row.original.name} />
        ),
      },
      {
        id: "name",
        header: "Name",
        accessorFn: productName,
        enableSorting: false,
        size: 220,
        Cell: ({ row }) => (
          <Box
            component="span"
            sx={
              row.original.name
                ? { fontWeight: 600 }
                : { color: "text.secondary", fontStyle: "italic" }
            }
          >
            {productName(row.original)}
          </Box>
        ),
      },
      {
        id: "category",
        header: "Category",
        accessorFn: (product) => product.category?.name ?? "",
        enableSorting: false,
        size: 200,
      },
      {
        id: "level",
        header: "Level",
        accessorFn: (product) => formatCode(product.level),
        enableSorting: false,
        size: 120,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (product) => formatCode(product.status),
        enableSorting: false,
        size: 140,
      },
      {
        id: "capabilities",
        header: "Capabilities",
        accessorFn: (product) =>
          product.capabilities.map((capability) => capability.name).join(", "),
        enableSorting: false,
        size: 420,
        Cell: ({ row }) => <CapabilityChips product={row.original} />,
      },
      {
        id: "source_count",
        header: "Sources",
        accessorKey: "source_count",
        sortDescFirst: true,
        size: 110,
        muiTableBodyCellProps: monospaceCell,
      },
      {
        id: "latest_source",
        header: "Latest source",
        accessorFn: (product) => product.latest_source ?? "",
        sortDescFirst: true,
        size: 150,
        muiTableBodyCellProps: monospaceCell,
      },
    ],
    [],
  );

  const sorting = useMemo(() => sortingOf(sort), [sort]);

  // A click on the header of the current order returns to relevance; a click
  // on another sortable header asks for that column's order.
  const handleSortingChange = (updater: MRT_Updater<MRT_SortingState>) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const column = next.find(({ id }) => id in SORT_OF_COLUMN)?.id;
    const asked = column ? SORT_OF_COLUMN[column] : "relevance";
    onSortChange(asked === sort ? "relevance" : asked);
  };

  return (
    <MaterialReactTable
      columns={columns}
      data={products}
      getRowId={(product) => product.id}
      enableSorting={true}
      manualSorting={true}
      enableMultiSort={false}
      onSortingChange={handleSortingChange}
      enableColumnFilters={false}
      enableGlobalFilter={false}
      enableColumnActions={false}
      enablePagination={false}
      enableBottomToolbar={false}
      enableTopToolbar={false}
      state={{ sorting, showProgressBars: loading }}
      initialState={{ density: "compact" }}
      muiTableBodyRowProps={({ row }) => {
        const open = row.original.id === openProductId;
        // The open row carries an accent outline, drawn inside the row
        const openProps = open
          ? {
              "aria-current": true as const,
              sx: {
                cursor: onOpen ? "pointer" : undefined,
                boxShadow: (theme: Theme) =>
                  `inset 0 0 0 2px ${theme.palette.link.main}`,
              },
            }
          : {};
        return onOpen
          ? {
              hover: true,
              onClick: () => onOpen(row.original),
              sx: { cursor: "pointer" },
              ...openProps,
            }
          : { hover: false, ...openProps };
      }}
      muiTablePaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: "background.raised",
          border: 1,
          borderColor: "divider",
        },
      }}
      muiTableProps={{
        sx: {
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "background.raised",
          },
          "& .MuiTableBody-root .MuiTableRow-root": {
            backgroundColor: "background.raised",
          },
        },
      }}
    />
  );
};

export default ProductResultTable;
